import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { EventService } from 'services/events.service';
import { Note } from 'interfaces/note.interface';
import { UnknownAction, Dispatch } from 'redux';
import DiaryNoteService from 'services/diaryNote.service';
import InputText from '@common_components/inputs/textInput.component';
import Button from '@common_components/buttons/button.component';
import { NewsPlusIcon } from '@common_components/icons/newsPlus.icon';
import { NewsIcon } from '@common_components/icons/news.icon';
import { NewsCheckIcon } from '@common_components/icons/newsCheck.icon';
import { NewsDeleteIcon } from '@common_components/icons/newsDelete.icon';
import { MAX_LENGTH_ENTITY } from 'constants/limite.constants';
import { CommonGlobalService, dayjs, RootState, usePersistantState, UUID } from '@umanforge/common';
import { useSelector } from 'react-redux';
import { VisualisationService } from 'services/visualisation.service';
import { KEY } from 'constants/local_storage_key.constant';

interface NotesSectionProps {
  selectedPage: number;
  onNoteAdded?: ( note: Note ) => void;
  dispatch: Dispatch<UnknownAction>;
  isAdminOfEventManager: boolean;
  eventId: string;
  isViewMode?: boolean;
  isArchived?: boolean;
  siteId: string;
  referenceEventId?: string;
  referenceEventTitle?: string;
  referenceEventNotes?: Note[];
  isCreationMode?: boolean;
  isParticipant?: boolean;
  setParentCurrentNotes?: React.Dispatch<React.SetStateAction<Note[]>>;
  onPendingNoteChange?: ( hasPendingNote: boolean ) => void;
  onTriggerNoteSend?: () => void;
}

const NotesSection: React.FC<NotesSectionProps> = ( {
  selectedPage,
  isViewMode,
  isArchived,
  dispatch,
  eventId,
  siteId,
  referenceEventTitle,
  referenceEventNotes = [],
  isCreationMode = false,
  onNoteAdded,
  isAdminOfEventManager,
  isParticipant,
  setParentCurrentNotes,
  onPendingNoteChange,
  onTriggerNoteSend
} ) => {
  const { t } = useTranslation();
  const me = useSelector( ( state: RootState ) => { return state.user.value; } );
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const visualisationToken = useSelector( ( state: RootState ) => { return state.persistedReducer.visualisationToken.value; } );

  const [ selectedSectorId ] = usePersistantState<UUID | undefined>( KEY.SELECTED_SECTOR_ID, undefined );

  const [ newNote, setNewNote ] = useState<string>( '' );
  const [ localNotes, setLocalNotes ] = useState<Note[]>( [] );
  const [ diaryLoading, setDiaryLoading ] = useState<{ [noteId: UUID]: boolean }>( {} );
  const [ pendingCreationNotes, setPendingCreationNotes ] = useState<string[]>( [] );

  const isAdmin =  isAdminOfEventManager || ( me != undefined && selectedSectorId != undefined && CommonGlobalService.isAdminOfSector( me, selectedSectorId ) );
  const disableInputs = !( isAdmin || isParticipant === true );

  useEffect( () => {
    if ( isCreationMode && typeof window !== 'undefined' ) {
      const typedWindow = window as WindowWithPendingNotes;
      if ( typedWindow._currentDraftNote ) {
        setNewNote( typedWindow._currentDraftNote );
      }
    }
  }, [ isCreationMode ] );

  useEffect( () => {
    if ( setParentCurrentNotes ) {
      setParentCurrentNotes( localNotes );
    }
  }, [ localNotes, setParentCurrentNotes ] );

  useEffect( () => {
    if ( isCreationMode && typeof window !== 'undefined' ) {
      ( window as WindowWithPendingNotes )._currentDraftNote = newNote;
    }
  }, [ newNote, isCreationMode ] );

  const fetchNotes = async () => {
    if ( isCreationMode ) {
      return;
    }

    try {
      const response: Note[] = [];
      if ( isViewMode ) {
        response.push( ...( await VisualisationService.getNotesByEventId( eventId, siteId, visualisationToken, dispatch ) ) );
      } else {
        response.push( ...( await EventService.getNotesByEventId( eventId, siteId, token, dispatch ) ) );
      }

      setLocalNotes( response );
    } catch ( error ) {
      console.error( 'Erreur lors du rafraîchissement des notes :', error );
    }
  };

  useEffect( () => {
    fetchNotes();
  }, [ eventId, token, siteId, dispatch, isCreationMode ] );

  useEffect( () => {
    if ( onPendingNoteChange ) {
      onPendingNoteChange( newNote.trim() !== '' );
    }
  }, [ newNote, onPendingNoteChange ] );

  const handleSendNote = useCallback( async () => {
    if ( !newNote.trim() ) {return;}

    if ( isCreationMode ) {
      const updatedPendingNotes = [ ...pendingCreationNotes, newNote ];
      setPendingCreationNotes( updatedPendingNotes );

      if ( typeof window !== 'undefined' ) {
        ( window as WindowWithPendingNotes )._temporaryNotes = updatedPendingNotes;
      }

      const newNoteObj = {
        id: `temp-${dayjs().valueOf()}`,
        value: newNote,
        creationDate: dayjs().toISOString(),
        disable: false,
        authorName: 'Moi',
        predecessorId: null,
        isOnSiteNoteDiary: false
      } as Note;

      setLocalNotes( prev => {
        if ( !Array.isArray( prev ) ) {
          prev = [];
        }
        return [
          ...prev,
          newNoteObj
        ];
      } );

      if ( onNoteAdded ) {
        onNoteAdded( newNoteObj );
      }

      setNewNote( '' );
      if ( typeof window !== 'undefined' ) {
        ( window as WindowWithPendingNotes )._currentDraftNote = '';
      }
      return;
    }

    const noteData = {
      value: newNote,
      disable: false,
      predecessorId: null,
    };
    try {
      const createdNote = await EventService.createNote( eventId, noteData, token, dispatch );

      if ( onNoteAdded && createdNote ) {
        onNoteAdded( createdNote );
      }

      await fetchNotes();
      setNewNote( '' );
      if ( typeof window !== 'undefined' ) {
        ( window as WindowWithPendingNotes )._currentDraftNote = '';
      }
    } catch ( error ) {
      console.error( 'Erreur lors de l\'envoi de la note :', error );
    }
  }, [ newNote, isCreationMode, pendingCreationNotes, onNoteAdded, eventId, token, dispatch, fetchNotes ] );

  useEffect( () => {
    if ( onTriggerNoteSend ) {
      ( window as WindowWithPendingNotes & { _handleSendNote?: () => void } )._handleSendNote = handleSendNote;
    }
  }, [ onTriggerNoteSend, handleSendNote ] );

  const handleAddToDiary = async ( noteId: string ) => {
    setDiaryLoading( prev => {return { ...prev, [ noteId ]: true };} );
    try {
      await DiaryNoteService.createDiaryNote(
        { noteId, siteId },
        token,
        dispatch
      );
      await fetchNotes();
    } catch ( error ) {
      console.error( 'Erreur lors de l\'ajout au Journal des notes :', error );
    } finally {
      setDiaryLoading( prev => {return { ...prev, [ noteId ]: false };} );
    }
  };  useEffect( () => {
    if ( isCreationMode && typeof window !== 'undefined' ) {
      const pendingNotesHandler: PendingNotesHandler = {
        getPendingNotes: () => { return pendingCreationNotes; },
        clearPendingNotes: () => {
          setPendingCreationNotes( [] );
          ( window as WindowWithPendingNotes )._temporaryNotes = [];
        }
      };

      ( window as WindowWithPendingNotes )._pendingNotesForEvent = pendingNotesHandler;

      ( window as WindowWithPendingNotes )._temporaryNotes = pendingCreationNotes;
    }

    return () => {
      if ( typeof window !== 'undefined' ) {
        delete ( window as WindowWithPendingNotes )._pendingNotesForEvent;
      }
    };
  }, [ isCreationMode, pendingCreationNotes, setPendingCreationNotes ] );

  useEffect( () => {
    if ( isCreationMode && typeof window !== 'undefined' ) {
      const typedWindow = window as WindowWithPendingNotes;

      if ( typedWindow._currentDraftNote ) {
        setNewNote( typedWindow._currentDraftNote );
      }

      if ( typedWindow._temporaryNotes && typedWindow._temporaryNotes.length > 0 ) {
        setPendingCreationNotes( typedWindow._temporaryNotes );

        const temporaryLocalNotes = typedWindow._temporaryNotes.map( ( noteValue: string ) => {
          return {
            id: `temp-${dayjs().valueOf()}-${Math.random().toString( 36 ).substring( 2, 9 )}`,
            value: noteValue,
            creationDate: dayjs().toISOString(),
            disable: false,
            authorName: 'Moi',
            predecessorId: null,
            isOnSiteNoteDiary: false
          } as Note;
        } );

        setLocalNotes( temporaryLocalNotes );
      }
    }

    return () => {
      if ( isCreationMode && typeof window !== 'undefined' ) {
        const typedWindow = window as WindowWithPendingNotes;

        if ( newNote && newNote.trim() ) {
          typedWindow._currentDraftNote = newNote;
        }

        typedWindow._temporaryNotes = pendingCreationNotes;
      }
    };
  }, [ isCreationMode, newNote, selectedPage, pendingCreationNotes, setNewNote, setPendingCreationNotes, setLocalNotes ] );

  useEffect( () => {
    if ( selectedPage === 1 && isCreationMode && typeof window !== 'undefined' ) {
      const typedWindow = window as WindowWithPendingNotes;
      if ( typedWindow._currentDraftNote ) {
        setNewNote( typedWindow._currentDraftNote );
      }

      if ( typedWindow._temporaryNotes && typedWindow._temporaryNotes.length > 0 ) {
        setPendingCreationNotes( typedWindow._temporaryNotes );

        const temporaryLocalNotes = typedWindow._temporaryNotes.map( ( noteValue: string ) => {
          return {
            id: `temp-${dayjs().valueOf()}-${Math.random().toString( 36 ).substring( 2, 9 )}`,
            value: noteValue,
            creationDate: dayjs().toISOString(),
            disable: false,
            authorName: 'Moi',
            predecessorId: null,
            isOnSiteNoteDiary: false
          } as Note;
        } );

        if ( temporaryLocalNotes.length !== ( Array.isArray( localNotes ) ? localNotes.length : 0 ) ) {
          setLocalNotes( temporaryLocalNotes );
        }
      }
    }
  }, [ selectedPage, isCreationMode, localNotes ] );

  const sortedNotes = Array.isArray( localNotes )
    ? [ ...localNotes ].sort( ( a, b ) => {return dayjs( b.creationDate ).valueOf() - dayjs( a.creationDate ).valueOf();} )
    : [];

  return (
    <div className="font-texte h-full w-full min-h-full">
      <div className="flex flex-col gap-4 w-full h-full items-center bg-white-base rounded-lg shadow-lg p-4">
        {
          ( isArchived || isViewMode ) == false ?
            <div className='flex flex-col gap-2 px-4 pt-4 w-full'>
              <label className="block text-sm font-medium text-gray-700">
                {t( 'translation:note' )}
              </label>
              <div className='w-full'>
                <InputText
                  type='textarea'
                  icon={<NewsIcon size={7}/>}
                  disable={( isArchived || isViewMode || disableInputs )}
                  value={newNote}
                  maxCharacter={ MAX_LENGTH_ENTITY.EVENT_NOTE }
                  onChange={( value: string ) => {
                    setNewNote( value );
                    if ( isCreationMode && typeof window !== 'undefined' ) {
                      ( window as WindowWithPendingNotes )._currentDraftNote = value;
                    }
                    return value;
                  }}
                />
              </div>
            </div>
            :
            null
        }
        <div className="flex-1 w-full overflow-y-auto bg-white rounded-xl p-4">

          {sortedNotes.length > 0 && (
            <div>
              {referenceEventNotes && referenceEventNotes.length > 0 && (
                <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-orange-50 rounded-lg">
                  <div className="text-orange-600 font-medium">
                    {t( 'translation:current_event_notes' )}
                  </div>
                </div>
              )}
              <div className="flex flex-col gap-2">
                {sortedNotes.map( ( note, index ) => {return (
                  <React.Fragment key={note.id || index}>
                    <div
                      className="flex-center border-2 group trans-fast hover:bg-[#faf6f6] rounded-xl p-2"
                    >
                      <div className='w-full'>
                        <p className="flex gap-2 items-center text-sm mb-2">
                          <span
                            className="truncate font-bold max-w-[250px] rounded-lg font-comfortaa px-4 bg-orange-200 text-orange-600 p-1"
                            title={note.authorName}
                          >
                            {note.authorName}
                          </span>
                          <span className="whitespace-nowrap text-gray-500">
                            {dayjs( note.creationDate ).format( 'L LT' )}
                          </span>
                        </p>
                        <span
                          className="p-2 w-full flex flex-col justify-center break-words"
                        >
                          {
                            note.value.split( '\n' ).map( ( line, lineIndex ) => {
                              return ( <div key={lineIndex}> { line } <br/> </div> );
                            } )
                          }
                        </span>
                        {( () => {
                          if ( note.disable && ( note.disableDate || note.disableAuthorName ) ) {
                            return (
                              <div className="w-full flex justify-start">
                                <span className="text-xs text-red-500">
                                  { t( 'removed_from_journal' ) }
                                  {note.disableAuthorName && ` ${t( 'by_user' )} ${note.disableAuthorName}`}
                                  {note.disableDate && ` ${t( 'on_date' )} ${dayjs( note.disableDate ).format( 'L LT' )}`}
                                </span>
                              </div>
                            );
                          }
                          if ( note.addUserId || note.addDate ) {
                            return (
                              <div className="w-full flex justify-start">
                                <span className="text-xs text-gray-500">
                                  { t( 'added_to_journal' ) }
                                  {typeof note.addUserFullName === 'string' && note.addUserFullName && ` ${t( 'by_user' )} ${note.addUserFullName}`}
                                  {note.addDate && ` ${t( 'on_date' )} ${dayjs( note.addDate ).format( 'L LT' )}`}
                                </span>
                              </div>
                            );
                          }
                          return null;
                        } )()}
                      </div>
                      {( !( isArchived || isViewMode || isCreationMode ) && isAdminOfEventManager ) && (
                        <div className="h-full flex-center">
                          {note.isOnSiteNoteDiary === false && !note.disable && (
                            <Button
                              tooltip={t( 'translation:add_to_diary' )}
                              icon={<NewsPlusIcon size={5} />}
                              padding='p-2'
                              textColor='text-green-700'
                              textColorHover='hover:text-green-700'
                              hoverBorderColor='hover:border-transparent border-green-600'
                              color='bg-green-200'
                              roundedRadius='rounded-xl'
                              shadow='shadow-none'
                              colorHover='hover:bg-green-300'
                              disable={!!diaryLoading[ note.id ]}
                              onClick={() => {
                                handleAddToDiary( note.id );
                              }}
                            />
                          )}
                          {note.isOnSiteNoteDiary === true && !note.disable && (
                            <div
                              className='p-2 flex-center'
                              title={t( 'translation:added_to_diary' )}
                            >
                              <NewsCheckIcon size={5} className='text-green-600' />
                            </div>
                          )}
                          {note.disable == true && (
                            <div
                              className='p-2 flex-center'
                              title={t( 'translation:removed_from_diary' )}
                            >
                              <NewsDeleteIcon size={5} className='text-red-600' />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </React.Fragment>
                );} )}
              </div>
            </div>
          )}

          {referenceEventNotes && referenceEventNotes.length > 0 && (
            <div className="mb-6 mt-4">
              <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-blue-50 rounded-lg">
                <div className="text-blue-600 font-medium">
                  {t( 'translation:reference_event_notes' )}
                </div>
                {referenceEventTitle && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-blue-700 font-medium px-2 py-1 rounded bg-blue-100">
                      {referenceEventTitle}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2 pl-3 border-l-4 border-blue-200">
                {referenceEventNotes.sort( ( a, b ) => {
                  return dayjs( b.creationDate ).valueOf() - dayjs( a.creationDate ).valueOf();
                } ).map( ( note, index ) => {
                  return (
                    <div
                      key={`ref-${note.id || index}`}
                      className="flex-center border-2 border-blue-100 group trans-fast hover:bg-blue-50 rounded-xl p-2"
                    >
                      <div className='w-full'>
                        <p className="flex gap-2 items-center text-sm mb-2">
                          <span
                            className="truncate font-bold max-w-[250px] rounded-lg font-comfortaa px-4 bg-blue-200 text-blue-600 p-1"
                            title={note.authorName}
                          >
                            {note.authorName}
                          </span>
                          <span className="whitespace-nowrap text-gray-500">
                            {dayjs( note.creationDate ).format( 'L LT' )}
                          </span>
                        </p>
                        <p
                          className="p-2 w-full flex items-center break-words"
                        >
                          {note.value}
                        </p>
                      </div>
                    </div>
                  );
                } )}
              </div>
            </div>
          )}

          {sortedNotes.length === 0 && ( !referenceEventNotes || referenceEventNotes.length === 0 ) && (
            <div className="px-4 text-gray-500 italic">{t( 'translation:no_note_available' )}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotesSection;

interface PendingNotesHandler {
  getPendingNotes: () => string[];
  clearPendingNotes: () => void;
}

interface WindowWithPendingNotes extends Window {
  _pendingNotesForEvent?: PendingNotesHandler;
  _currentDraftNote?: string;
  _temporaryNotes?: string[];
}
